/**
 * EmployerNoMembershipStub.jsx
 *
 * RC 1.62 — Empty-workspace stub state for employer-side users who
 * authenticated successfully but are NOT yet attached to any
 * employer organisation.
 *
 * Rendered when:
 *   • `GET /api/employer/v1/me` returns 404 ("No organisation yet")
 *   • or `GET /api/employer/v1/me` returns a payload with no
 *     `organisation` field (defensive fallback)
 *
 * Doctrine:
 *   - Employer organisations are admin/founding-access controlled.
 *   - Public org creation is OFF (PUBLIC_ORG_CREATION_ENABLED=false).
 *   - The only legitimate paths for a stub-state user to gain
 *     access are: open the invitation link they were sent, OR ask
 *     a platform admin.
 *   - This page MUST NOT offer org-creation, role-management, or
 *     any workspace data. It is a calm, terminal stub.
 *
 * Strict:
 *   - no email composition
 *   - no Stripe / pricing
 *   - no automatic workspace access
 *   - no UI redesign (uses existing shadcn Card + dark theme tokens)
 */
import { Briefcase, ShieldCheck } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';


export function EmployerNoMembershipStub({ orgName = null }) {
  const { signOut } = useAuth();

  return (
    <div
      className="mx-auto max-w-2xl px-4 py-12 sm:py-16"
      data-testid="employer-no-membership-stub"
    >
      <Card className="border-ndor-edge bg-ndor-surface p-8">
        <div className="flex items-start gap-4">
          <div className="rounded-md border border-ndor-edge bg-ndor-surface-alt p-3">
            <Briefcase className="h-6 w-6 text-ndor-ink-secondary" />
          </div>
          <div className="flex-1">
            <h1
              className="text-xl font-serif-display font-semibold tracking-tight text-ndor-ink"
              data-testid="employer-no-membership-stub-title"
            >
              You are not connected to an employer workspace yet
            </h1>
            <p className="mt-3 text-sm text-ndor-ink-secondary leading-relaxed">
              If you were invited to join an organisation, open the
              invitation link from the original message. If you
              believe this is an error, contact the person who
              invited you or your platform administrator.
            </p>

            <div className="mt-6 rounded border border-ndor-edge bg-ndor-surface-alt/60 p-4 text-xs text-ndor-ink-muted leading-relaxed">
              <p className="flex items-start gap-2">
                <ShieldCheck className="h-3.5 w-3.5 mt-0.5 shrink-0 text-ndor-gold" />
                <span>
                  NDORFlow employer organisations are
                  admin-provisioned. Account creation never creates
                  an organisation automatically — workspaces are
                  granted only through a valid invitation.
                </span>
              </p>
            </div>

            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
              <Button
                variant="outline"
                onClick={signOut}
                data-testid="employer-no-membership-stub-signout"
              >
                Sign out
              </Button>
            </div>

            {orgName && (
              <p className="mt-6 text-[11px] text-ndor-ink-muted/70 font-mono">
                {orgName}
              </p>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}

export default EmployerNoMembershipStub;
